/**
 * Currency context & hook for dynamic base-currency support.
 *
 * All financial values in the app are stored in ILS. This provider
 * converts them to the user's chosen display currency using live
 * exchange rates from the `fiat` data loaded in root.tsx.
 *
 * Usage:
 *   1. Wrap the app in <CurrencyProvider currency={...} fiatRates={...}>
 *   2. In any component:
 *        const { currency, setCurrency, formatValue, convert, symbol } = useCurrency();
 *        <span>{formatValue(ilsAmount)}</span>   // converts + formats
 *        <span>{convert(ilsAmount)}</span>        // converts only (number)
 */

import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import type { Currency } from "~/utils/currency";
import type { Fiat } from "~/types/global.types";
import {
  DEFAULT_CURRENCY,
  writeCurrencyCookie,
  CURRENCY_SYMBOL,
  formatCurrencyValue,
  createCurrencyFormatter,
} from "~/utils/currency";
import { convertCurrency } from "~/calculations/genericCalculations/convertCurrency";

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

interface CurrencyContextValue {
  /** Current active currency code ("ILS" | "USD" | "EUR" | "GBP"). */
  currency: Currency;
  /** Switch currency – writes cookie and reloads for SSR consistency. */
  setCurrency: (currency: Currency) => void;
  /** The symbol for the current currency (e.g. "₪", "$"). */
  symbol: string;
  /**
   * Convert an ILS amount to the display currency (number).
   * Returns the original value when display currency is ILS or rates unavailable.
   */
  convert: (ilsValue: number) => number;
  /**
   * Convert a display-currency amount back to ILS (number).
   * Inverse of convert(). Used when user edits an amount in the display currency.
   */
  unconvert: (displayValue: number) => number;
  /**
   * Convert an ILS amount to the display currency and format it as a string.
   */
  formatValue: (
    ilsValue: number,
    options?: {
      maximumFractionDigits?: number;
      minimumFractionDigits?: number;
      compact?: boolean;
      signDisplay?: Intl.NumberFormatOptions["signDisplay"];
    },
  ) => string;
  /** A reusable Intl.NumberFormat instance for the current currency. */
  formatter: Intl.NumberFormat;
}

const CurrencyContext = createContext<CurrencyContextValue>({
  currency: DEFAULT_CURRENCY,
  setCurrency: () => {},
  symbol: CURRENCY_SYMBOL[DEFAULT_CURRENCY],
  convert: (v) => v,
  unconvert: (v) => v,
  formatValue: (v) => formatCurrencyValue(v, DEFAULT_CURRENCY),
  formatter: createCurrencyFormatter(DEFAULT_CURRENCY),
});

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

interface CurrencyProviderProps {
  /** Currency determined on the server (from cookie). */
  currency: Currency;
  /** Live fiat exchange rates. Passed from root state once loaded. */
  fiatRates?: Fiat[];
  children: ReactNode;
}

export function CurrencyProvider({ currency, fiatRates, children }: CurrencyProviderProps) {
  const setCurrency = useCallback((newCurrency: Currency) => {
    writeCurrencyCookie(newCurrency);
    window.location.reload();
  }, []);

  const symbol = CURRENCY_SYMBOL[currency];

  const ilsFiat = useMemo(
    () => fiatRates?.find((f) => f.value === "ILS"),
    [fiatRates],
  );

  const targetFiat = useMemo(
    () => fiatRates?.find((f) => f.value === currency),
    [fiatRates, currency],
  );

  const convert = useCallback(
    (ilsValue: number): number => {
      if (currency === "ILS" || !ilsFiat || !targetFiat) return ilsValue;
      return convertCurrency(ilsValue, targetFiat, ilsFiat);
    },
    [currency, ilsFiat, targetFiat],
  );

  const unconvert = useCallback(
    (displayValue: number): number => {
      if (currency === "ILS" || !ilsFiat || !targetFiat) return displayValue;
      return convertCurrency(displayValue, ilsFiat, targetFiat);
    },
    [currency, ilsFiat, targetFiat],
  );

  const formatValue = useCallback(
    (
      ilsValue: number,
      options?: {
        maximumFractionDigits?: number;
        minimumFractionDigits?: number;
        compact?: boolean;
        signDisplay?: Intl.NumberFormatOptions["signDisplay"];
      },
    ) => formatCurrencyValue(convert(ilsValue), currency, options),
    [convert, currency],
  );

  const formatter = useMemo(
    () => createCurrencyFormatter(currency),
    [currency],
  );

  return (
    <CurrencyContext.Provider value={{ currency, setCurrency, symbol, convert, unconvert, formatValue, formatter }}>
      {children}
    </CurrencyContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useCurrency() {
  return useContext(CurrencyContext);
}
